import { Input, InputWrapperProps } from "@mantine/core";
import {
  DateInput,
  DatePickerProps,
  DateInputProps,
  DateTimePicker,
  DateTimePickerProps,
} from "@mantine/dates";
import { IconCalendar, IconCalendarClock } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useFormState } from "react-hook-form";
import { useFormHelperContext } from "../form";
import { InputWrapper } from "../inputWrapper";

export interface FieldInputDatePickerProps extends DatePickerProps {
  label: string;
  name: string;
  placeholder?: string;
  inputWrapper?: InputWrapperProps;
  withAsterisk?: boolean;
  readOnly?: boolean;
  required?: boolean;
}

// export const FieldDatePicker: React.FC<FieldInputDatePickerProps> = ({
//   label,
//   name,
//   placeholder,
//   inputWrapper,
//   withAsterisk,
//   readOnly,
//   required,
//   ...rest
// }) => {
//   const { register, control, formStore, setValue, getValues } =
//     useFormHelperContext();
//   const { errors } = useFormState({ control });
//   const currentValue = getValues(name);
//   const inputRef = useRef<HTMLInputElement | null>(currentValue);
//   const isFocused = useRef(false);
//   const [valuePlaceholder, setValuePlaceholder] = useState<Date | null>(
//     currentValue ? dayjs(currentValue).toDate() : null
//   );
//   if (!register) {
//     throw new Error("Field.DatePicker must be used within a FormHelper component");
//   }


//   useEffect(() => {
//     if (currentValue instanceof Date && valuePlaceholder instanceof Date) {
//       if (typeof currentValue === 'object' && currentValue !== null && 'toISOString' in currentValue) {
//         if (typeof valuePlaceholder === 'object' && valuePlaceholder !== null && 'toISOString' in valuePlaceholder) {
//           const placeholderISO = valuePlaceholder?.toISOString();
//           if (currentValue.toISOString() !== placeholderISO) {
//             setValuePlaceholder(currentValue);
//             setValue(name, currentValue);
//           }
//         }
//       }
//     }
//   }, [currentValue])

//   useEffect(() => {
//     if (isFocused.current && inputRef.current) {
//       inputRef.current.focus();
//     }
//   }, [errors]);

//   return (
//     <InputWrapper label={label} {...inputWrapper} withAsterisk={withAsterisk}>
//       <div className="flex flex-col w-full">
//         <DateInput
//           defaultValue={currentValue}
//           value={valuePlaceholder}
//           placeholder={placeholder}
//           disabled={readOnly ?? formStore.readOnly}
//           onChange={(value: Date | null) => {
//             setValuePlaceholder(value);
//             setValue(name, value);
//           }}
//           data-testid={'field-datepicker-' + name}
//           required={required}
//           rightSection={<IconCalendar size={22} />}
//           {...(rest as DateInputProps)}
//         />
//         {typeof errors[name]?.message === "string" && (
//           <Input.Error>{errors[name]?.message}</Input.Error>
//         )}
//       </div>
//     </InputWrapper>
//   );
// };


export const FieldDatePicker: React.FC<FieldInputDatePickerProps> = ({
  label,
  name,
  placeholder,
  inputWrapper,
  withAsterisk,
  readOnly,
  required,
  ...rest
}) => {
  const { register, control, formStore, setValue, getValues } =
    useFormHelperContext();
  const { errors } = useFormState({ control });
  const currentValue = getValues(name);
  const inputRef = useRef<HTMLInputElement | null>(currentValue);
  const isFocused = useRef(false);
  const [valuePlaceholder, setValuePlaceholder] = useState<Date | null>(
    currentValue ? makeFakeLocalDateFromUTCString(currentValue) : null
  );
  if (!register) {
    throw new Error("Field.DatePicker must be used within a FormHelper component");
  }


  // useEffect(() => {
  //   if (currentValue instanceof Date && valuePlaceholder instanceof Date) {
  //     if (typeof currentValue === 'object' && currentValue !== null && 'toISOString' in currentValue) {
  //       if (typeof valuePlaceholder === 'object' && valuePlaceholder !== null && 'toISOString' in valuePlaceholder) {
  //         const placeholderISO = valuePlaceholder?.toISOString();
  //         if (currentValue.toISOString() !== placeholderISO) {
  //           setValuePlaceholder(currentValue);
  //           setValue(name, currentValue);
  //         }
  //       }
  //     }
  //   }
  // }, [currentValue])

  // Sinkronisasi jika currentValue berubah dari luar form
  useEffect(() => {
    const newFakeDate = makeFakeLocalDateFromUTCString(currentValue);
    if (
      (newFakeDate && !valuePlaceholder) ||
      (!newFakeDate && valuePlaceholder) ||
      (newFakeDate && valuePlaceholder && newFakeDate.getTime() !== valuePlaceholder.getTime())
    ) {
      setValuePlaceholder(newFakeDate);
    }
  }, [currentValue]);


  useEffect(() => {
    if (isFocused.current && inputRef.current) {
      inputRef.current.focus();
    }
  }, [errors]);


  const handleChange = (newValue: Date | null) => {
    if (newValue) {
      const updatedValue = new Date(newValue);
      // Konversi updatedValue "fake local" ke UTC ISO string
      const utcString = dayjs.utc()
        .year(updatedValue.getFullYear())
        .month(updatedValue.getMonth())
        .date(updatedValue.getDate())
        .hour(updatedValue.getHours())
        .minute(updatedValue.getMinutes())
        .second(updatedValue.getSeconds())
        .millisecond(updatedValue.getMilliseconds())
        .toISOString();

      setValue(name, utcString);
      setValuePlaceholder(updatedValue);
    } else {
      setValue(name, null);
      setValuePlaceholder(null);
    }
  };

  return (
    <InputWrapper label={label} {...inputWrapper} withAsterisk={withAsterisk}>
      <div className="flex flex-col w-full">
        <DateInput
          defaultValue={currentValue}
          value={valuePlaceholder}
          placeholder={placeholder}
          disabled={readOnly ?? formStore.readOnly}
          onChange={handleChange}
          data-testid={'field-datepicker-' + name}
          required={required}
          rightSection={<IconCalendar size={22} />}
          {...(rest as DateInputProps)}
        />
        {typeof errors[name]?.message === "string" && (
          <Input.Error>{errors[name]?.message}</Input.Error>
        )}
      </div>
    </InputWrapper>
  );
};

export interface FieldInputDateTimePickerProps extends DateTimePickerProps {
  label: string;
  name: string;
  placeholder?: string;
  inputWrapper?: InputWrapperProps;
  withAsterisk?: boolean;
  readOnly?: boolean;
}

// export const FieldDateTimePicker: React.FC<FieldInputDateTimePickerProps> = ({
//   label,
//   name,
//   placeholder,
//   inputWrapper,
//   withAsterisk,
//   readOnly,
//   ...rest
// }) => {
//   const { register, control, formStore, setValue, getValues } =
//     useFormHelperContext();
//   const { errors } = useFormState({ control });
//   const currentValue = getValues(name);
//   const inputRef = useRef<HTMLInputElement | null>(currentValue);
//   const isFocused = useRef(false);
//   const [valuePlaceholder, setValuePlaceholder] = useState<Date | null>(
//     currentValue ? dayjs(currentValue).toDate() : null
//   );
//   const lastValueRef = useRef<Date | null>(null);

//   if (!register) {
//     throw new Error(
//       "Field.DateTimePicker must be used within a FormHelper component"
//     );
//   }

//   useEffect(() => {
//     if (isFocused.current && inputRef.current) {
//       inputRef.current.focus();
//     }
//   }, [errors]);

//   useEffect(() => {
//     if (currentValue instanceof Date && valuePlaceholder instanceof Date) {
//       if (typeof currentValue === 'object' && currentValue !== null && 'toISOString' in currentValue) {
//         if (typeof valuePlaceholder === 'object' && valuePlaceholder !== null && 'toISOString' in valuePlaceholder) {
//           const placeholderISO = valuePlaceholder?.toISOString();
//           if (currentValue.toISOString() !== placeholderISO) {
//             setValuePlaceholder(currentValue);
//             setValue(name, currentValue);
//           }
//         }
//       }
//     }
//   }, [currentValue])

//   const handleChange = (newValue: Date | null) => {
//     if (newValue) {
//       const prev = lastValueRef.current;

//       const isTimeUnchanged =
//         !prev ||
//         (prev.getHours() === 0 &&
//           prev.getMinutes() === 0 &&
//           prev.getSeconds() === 0 &&
//           prev.getMilliseconds() === 0);

//       const updatedValue = new Date(newValue);

//       // Hanya set jam ke 23:59:59 jika sebelumnya belum ada jam atau jam masih default
//       if (isTimeUnchanged) {
//         updatedValue.setHours(23, 59, 59, 999);
//       }

//       setValue(name, updatedValue);
//       setValuePlaceholder(updatedValue);
//       lastValueRef.current = updatedValue;
//     } else {
//       setValue(name, null);
//       setValuePlaceholder(null);
//       lastValueRef.current = null;
//     }
//   };

//   return (
//     <InputWrapper label={label} {...inputWrapper} withAsterisk={withAsterisk}>
//       <div className="flex flex-col w-full">
//         <DateTimePicker
//           defaultValue={currentValue}
//           value={valuePlaceholder}
//           placeholder={placeholder}
//           disabled={readOnly ?? formStore.readOnly}
//           onChange={handleChange}
//           {...rest}
//           rightSection={<IconCalendarClock size={22} />}
//         />
//         {typeof errors[name]?.message === "string" && (
//           <Input.Error>{errors[name]?.message}</Input.Error>
//         )}
//       </div>
//     </InputWrapper>
//   );
// };


// export const FieldDateTimePicker: React.FC<FieldInputDateTimePickerProps> = ({
//   label,
//   name,
//   placeholder,
//   inputWrapper,
//   withAsterisk,
//   readOnly,
//   ...rest
// }) => {
//   const { register, control, formStore, setValue, getValues } =
//     useFormHelperContext();
//   const { errors } = useFormState({ control });
//   const currentValue = getValues(name); // Bisa string ISO UTC atau Date atau null

//   // Buat fake local Date dari currentValue (UTC string) supaya jamnya tetap sama
//   const makeFakeLocalDateFromUTCString = (utcString: string | Date | null): Date | null => {
//     if (!utcString) return null;
//     const d = typeof utcString === "string" ? dayjs.utc(utcString) : dayjs.utc(utcString.toISOString());
//     return new Date(d.year(), d.month(), d.date(), d.hour(), d.minute(), d.second(), d.millisecond());
//   };

//   const [valuePlaceholder, setValuePlaceholder] = useState<Date | null>(
//     makeFakeLocalDateFromUTCString(currentValue)
//   );
//   const lastValueRef = useRef<Date | null>(null);

//   if (!register) {
//     throw new Error(
//       "Field.DateTimePicker must be used within a FormHelper component"
//     );
//   }

//   useEffect(() => {
//     // Sync jika currentValue berubah dari luar form
//     const newFakeDate = makeFakeLocalDateFromUTCString(currentValue);
//     if (
//       newFakeDate?.getTime() !== valuePlaceholder?.getTime()
//     ) {
//       setValuePlaceholder(newFakeDate);
//     }
//   }, [currentValue]);

//   const handleChange = (newValue: Date | null) => {
//     if (newValue) {
//       // Cek apakah waktu belum diset (jam, menit, detik = 0)
//       const prev = lastValueRef.current;
//       const isTimeUnchanged =
//         !prev ||
//         (prev.getHours() === 0 &&
//           prev.getMinutes() === 0 &&
//           prev.getSeconds() === 0 &&
//           prev.getMilliseconds() === 0);

//       const updatedValue = new Date(newValue);

//       if (isTimeUnchanged) {
//         updatedValue.setHours(23, 59, 59, 999);
//       }

//       // Konversi updatedValue yang "fake local" ke UTC ISO string untuk disimpan
//       const utcString = dayjs.utc()
//         .year(updatedValue.getFullYear())
//         .month(updatedValue.getMonth())
//         .date(updatedValue.getDate())
//         .hour(updatedValue.getHours())
//         .minute(updatedValue.getMinutes())
//         .second(updatedValue.getSeconds())
//         .millisecond(updatedValue.getMilliseconds())
//         .toISOString();

//       setValue(name, utcString);
//       setValuePlaceholder(updatedValue);
//       lastValueRef.current = updatedValue;
//     } else {
//       setValue(name, null);
//       setValuePlaceholder(null);
//       lastValueRef.current = null;
//     }
//   };

//   return (
//     <InputWrapper label={label} {...inputWrapper} withAsterisk={withAsterisk}>
//       <div className="flex flex-col w-full">
//         <DateTimePicker
//           value={valuePlaceholder}
//           placeholder={placeholder}
//           disabled={readOnly ?? formStore.readOnly}
//           onChange={handleChange}
//           valueFormat="YYYY-MM-DD HH:mm:ss [UTC]"
//           {...rest}
//           rightSection={<IconCalendarClock size={22} />}
//         />
//         {typeof errors[name]?.message === "string" && (
//           <Input.Error>{errors[name]?.message}</Input.Error>
//         )}
//       </div>
//     </InputWrapper>
//   );
// };


export const FieldDateTimePicker: React.FC<FieldInputDateTimePickerProps> = ({
  label,
  name,
  placeholder,
  inputWrapper,
  withAsterisk,
  readOnly,
  ...rest
}) => {
  const { register, control, formStore, setValue, getValues } =
    useFormHelperContext();
  const { errors } = useFormState({ control });
  const currentValue = getValues(name); // Bisa string ISO UTC atau Date atau null

  // Fungsi membuat "fake local date" dari UTC ISO string atau Date
  const [valuePlaceholder, setValuePlaceholder] = useState<Date | null>(
    makeFakeLocalDateFromUTCString(currentValue)
  );
  const lastValueRef = useRef<Date | null>(null);

  if (!register) {
    throw new Error(
      "Field.DateTimePicker must be used within a FormHelper component"
    );
  }

  // Sinkronisasi jika currentValue berubah dari luar form
  useEffect(() => {
    const newFakeDate = makeFakeLocalDateFromUTCString(currentValue);
    if (
      (newFakeDate && !valuePlaceholder) ||
      (!newFakeDate && valuePlaceholder) ||
      (newFakeDate && valuePlaceholder && newFakeDate.getTime() !== valuePlaceholder.getTime())
    ) {
      setValuePlaceholder(newFakeDate);
    }
  }, [currentValue]);

  const handleChange = (newValue: Date | null) => {
    if (newValue) {
      const prev = lastValueRef.current;
      const isTimeUnchanged =
        !prev ||
        (prev.getHours() === 0 &&
          prev.getMinutes() === 0 &&
          prev.getSeconds() === 0 &&
          prev.getMilliseconds() === 0);

      const updatedValue = new Date(newValue);

      if (isTimeUnchanged) {
        updatedValue.setHours(23, 59, 59, 999);
      }

      // Konversi updatedValue "fake local" ke UTC ISO string
      const utcString = dayjs.utc()
        .year(updatedValue.getFullYear())
        .month(updatedValue.getMonth())
        .date(updatedValue.getDate())
        .hour(updatedValue.getHours())
        .minute(updatedValue.getMinutes())
        .second(updatedValue.getSeconds())
        .millisecond(updatedValue.getMilliseconds())
        .toISOString();

      setValue(name, utcString);
      setValuePlaceholder(updatedValue);
      lastValueRef.current = updatedValue;
    } else {
      setValue(name, null);
      setValuePlaceholder(null);
      lastValueRef.current = null;
    }
  };

  return (
    <InputWrapper label={label} {...inputWrapper} withAsterisk={withAsterisk}>
      <div className="flex flex-col w-full">
        <DateTimePicker
          value={valuePlaceholder}
          placeholder={placeholder}
          disabled={readOnly ?? formStore.readOnly}
          onChange={handleChange}
          valueFormat="YYYY-MM-DD HH:mm:ss"
          {...rest}
          rightSection={<IconCalendarClock size={22} />}
        />
        {typeof errors[name]?.message === "string" && (
          <Input.Error>{errors[name]?.message}</Input.Error>
        )}
      </div>
    </InputWrapper>
  );
};


export const makeFakeLocalDateFromUTCString = (utcString: string | Date | null): Date | null => {
  if (!utcString) return null;
  const d = typeof utcString === "string" ? dayjs.utc(utcString) : dayjs.utc(dayjs(utcString).toISOString());
  return new Date(d.year(), d.month(), d.date(), d.hour(), d.minute(), d.second(), d.millisecond());
};
